import { Permission, hasPermission } from "./permissions";

/**
 * Which permission gates which tab of each monitoring control panel — the ONE
 * place that says what a panel is made of.
 *
 * Read by BOTH the panels' own tab lists and by "may this panel open at all"
 * (`canOpenControlPanel`), so the two cannot drift: a panel opens exactly when
 * at least one of its tabs would render. The Roles editor's Monitoring Dashboard
 * blocks (permissionBundles.ts) are keyed on these same gates.
 *
 * Verified against the tab lists in the components (2026-09-18):
 *  - ControlPanelBank — Actions, Faults, Scheduled Actions.
 *  - ControlPanelLift — Actions (which also carries the scheduled-actions
 *    section), Faults, Status (ELEVATOR only), State.
 *
 * NOT here, on purpose: `monitor-control-panel-vip-mode`. No tab in either panel
 * reads it — VIP runs as a sequenced action inside the Actions tab — so
 * holding it must never make a name clickable. Nor Scheduled Actions for the
 * LIFT panel: there it is a section of the Actions tab, not a tab of its own,
 * so it cannot open a lift panel by itself.
 */
export type ControlPanelKind = "bank" | "lift";

export const CONTROL_PANEL_TAB_GATES: Record<ControlPanelKind, readonly Permission[]> = {
  bank: [
    Permission.MonitorControlPanelActions,
    Permission.MonitorControlPanelFaults,
    Permission.ScheduledActionsView,
  ],
  lift: [
    Permission.MonitorControlPanelActions,
    Permission.MonitorControlPanelFaults,
    Permission.MonitorControlPanelStatus,
    Permission.MonitorControlPanelState,
  ],
};

/**
 * A panel may open only when at least one of its tabs would render. With none,
 * the name stays inert (a permission denial is not an error, so it says
 * nothing) and the drawer itself refuses to open.
 */
export function canOpenControlPanel(
  kind: ControlPanelKind,
  permissionSet: string[],
  isMaster: boolean,
): boolean {
  if (isMaster) return true;
  return CONTROL_PANEL_TAB_GATES[kind].some((gate) => hasPermission(gate, permissionSet));
}

/**
 * The tab a lift control panel should land on for this role, when nothing more
 * specific (a live fault) applies. Actions first, then the tabs in the order
 * they read on screen. Shared by every lift view that opens the panel, so the
 * State tab — which has no other way in — is not forgotten in one of them.
 */
export function defaultLiftPanelTab(permissionSet: string[]): string {
  if (hasPermission(Permission.MonitorControlPanelActions, permissionSet)) return "ACTIONS";
  if (hasPermission(Permission.MonitorControlPanelCarCalls, permissionSet)) return "CARCALLS";
  if (hasPermission(Permission.MonitorControlPanelHallCalls, permissionSet)) return "HALLCALLS";
  if (hasPermission(Permission.MonitorControlPanelFaults, permissionSet)) return "FAULTS";
  if (hasPermission(Permission.MonitorControlPanelStatus, permissionSet)) return "STATUS";
  if (hasPermission(Permission.MonitorControlPanelState, permissionSet)) return "STATE";
  return "CARCALLS";
}
